/**
 * Traveller-facing departure card copy — uses API traveller_* fields when present,
 * falls back to deposit-paid counts only (never pending checkout).
 */

export const TRAVELLER_STATUS = {
	OPEN: 'open',
	FORMING: 'forming',
	GUARANTEED: 'guaranteed',
	SOLD_OUT: 'sold_out',
	CLOSED: 'closed',
};

function paidReservations(departure) {
	if (departure.paid_reservations != null) return departure.paid_reservations;
	if (departure.deposit_paid_count != null) return departure.deposit_paid_count;
	// Never use reserved_people_count — it includes unpaid pending_payment checkout attempts.
	return departure.paid_people_count || 0;
}

function needMore(departure) {
	if (departure.need_more_to_guarantee != null) return departure.need_more_to_guarantee;
	const min = departure.min_group_size || 6;
	return Math.max(0, min - paidReservations(departure));
}

function resolveKind(departure) {
	if (departure.traveller_status) return departure.traveller_status;
	const paid = paidReservations(departure);
	const min = departure.min_group_size || 6;
	const seats = departure.seats_remaining ?? departure.spots_left;
	if (departure.registration_closed || departure.slot_closed) return TRAVELLER_STATUS.CLOSED;
	if (seats != null && seats <= 0) return TRAVELLER_STATUS.SOLD_OUT;
	if (departure.accepting_registrations === false) return TRAVELLER_STATUS.SOLD_OUT;
	if (departure.group_formed || departure.rental_formed || paid >= min) return TRAVELLER_STATUS.GUARANTEED;
	if (paid > 0) return TRAVELLER_STATUS.FORMING;
	return TRAVELLER_STATUS.OPEN;
}

function statusLabel(kind, departure) {
	if (departure.traveller_status_label) return departure.traveller_status_label;
	switch (kind) {
	case TRAVELLER_STATUS.GUARANTEED: return 'Guaranteed departure';
	case TRAVELLER_STATUS.FORMING: return 'Forming group';
	case TRAVELLER_STATUS.OPEN: return 'Open departure';
	case TRAVELLER_STATUS.SOLD_OUT: return 'Sold out';
	case TRAVELLER_STATUS.CLOSED: return 'Registration closed';
	default: return 'Departure';
	}
}

function guaranteeMessage(kind, departure) {
	if (departure.guarantee_message) return departure.guarantee_message;
	const paid = paidReservations(departure);
	const need = needMore(departure);
	const min = departure.min_group_size || 6;
	switch (kind) {
	case TRAVELLER_STATUS.GUARANTEED:
		return 'This departure is guaranteed to run.';
	case TRAVELLER_STATUS.FORMING:
		if (paid === 1) {
			return `1 traveller has reserved. Need ${need} more ${need === 1 ? 'traveller' : 'travellers'} to guarantee this departure.`;
		}
		return `${paid} travellers have reserved. Need ${need} more ${need === 1 ? 'traveller' : 'travellers'} to guarantee this departure.`;
	case TRAVELLER_STATUS.OPEN:
		return `Be the first to reserve — ${min} ${min === 1 ? 'traveller' : 'travellers'} needed to guarantee this departure.`;
	case TRAVELLER_STATUS.SOLD_OUT:
		return 'This departure is full. Choose another month or trek.';
	case TRAVELLER_STATUS.CLOSED:
		return 'Registration has closed for this departure date.';
	default:
		return '';
	}
}

function capacityMessage(departure) {
	if (departure.capacity_message) return departure.capacity_message;
	const max = departure.max_group_size;
	if (max == null) return null;
	const seats = departure.seats_remaining ?? departure.spots_left;
	if (seats == null) return `Maximum group size: ${max} travellers`;
	if (seats <= 0) return 'No seats remaining';
	if (seats === 1) return `Maximum group size: ${max} travellers · Only 1 seat remaining`;
	return `Maximum group size: ${max} travellers · ${seats} seats still available`;
}

function reserveCta(kind, departure) {
	if (departure.reserve_cta) return departure.reserve_cta;
	if (kind === TRAVELLER_STATUS.GUARANTEED) return 'Join this departure';
	if (kind === TRAVELLER_STATUS.SOLD_OUT || kind === TRAVELLER_STATUS.CLOSED) return 'See other departures';
	return 'Reserve my spot';
}

function guaranteeProgress(departure, kind) {
	const paid = paidReservations(departure);
	const min = departure.min_group_size || 6;
	if (kind === TRAVELLER_STATUS.GUARANTEED) return 100;
	return Math.min(100, Math.round((paid / min) * 100));
}

/** Unified copy object for all public departure surfaces. */
export function getDepartureCardCopy(departure = {}) {
	const kind = resolveKind(departure);
	const paid = paidReservations(departure);
	const need = needMore(departure);
	const min = departure.min_group_size || 6;
	const canReserve = departure.can_reserve != null
		? departure.can_reserve
		: (departure.accepting_registrations !== false && (departure.spots_left == null || departure.spots_left > 0));

	return {
		kind,
		statusLabel: statusLabel(kind, departure),
		guaranteeMessage: guaranteeMessage(kind, departure),
		capacityMessage: capacityMessage(departure),
		reserveCta: reserveCta(kind, departure),
		depositLine: 'Small deposit today · remaining balance after the group is confirmed',
		paidReservations: paid,
		needMoreToGuarantee: need,
		minGroupSize: min,
		progress: guaranteeProgress(departure, kind),
		canReserve,
		showCapacity: kind !== TRAVELLER_STATUS.SOLD_OUT && kind !== TRAVELLER_STATUS.CLOSED,
	};
}

export default getDepartureCardCopy;
